import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Banknote, Hash, X } from 'lucide-react';
import { apiUrl } from '../config/api';
import KpiCard from '../components/Admin/KpiCard';
import ResponsiveTable from '../components/ResponsiveTable';
import LoanLink from '../components/LoanLink';
import ClientLink from '../components/ClientLink';
import RepaymentLink from '../components/RepaymentLink';
import { useDetailsModal } from '../context/DetailsModalContext';
import { toCurrency } from '../utils/adminMetrics';
import { formatDateDMY } from '../utils/dateUtils';
import UndoRepaymentConfirm from '../components/UndoRepaymentConfirm';
import { AuthContext } from '../context/AuthContext';
import Pager from '../components/UI/Pager';

const PAGE_SIZE = 12;
const SORTS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'largest', label: 'Largest amount' },
  { value: 'smallest', label: 'Smallest amount' },
];
// Filters kept in the address, so a link can open the page already filtered:
// /repayments?search=&method=&from=&to=&sort=&page=&loan_id=&client_id=
const KEYS = ['search', 'method', 'from', 'to', 'sort', 'page', 'loan_id', 'client_id'];


// Every repayment across all loans, from GET /api/repayments: the totals of
// what is filtered on top, then the payments as cards, a page at a time.
const Repayments = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { openRepayment } = useDetailsModal();
  const query = useMemo(() => {
    const q = {};
    KEYS.forEach((k) => { const v = searchParams.get(k); if (v) q[k] = v; });
    return q;
  }, [searchParams]);

  const [searchText, setSearchText] = useState(query.search || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Undo a repayment, or put back an undone one (the same form as the Loans
  // page); the list reloads after. Putting back is for staff who can enter
  // repayments.
  const [undoCtx, setUndoCtx] = useState(null);
  const { user } = useContext(AuthContext) || {};
  const userPerms = user?.permissions || [];
  const canRedo = ['*', 'EnterRepayment', 'ManageLoans'].some((p) => userPerms.includes(p));
  const [reloadKey, setReloadKey] = useState(0);
  const [notice, setNotice] = useState('');

  // Change some filters in the address; any change but the page goes back to page 1.
  const setFilters = (changes, { keepPage = false } = {}) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') next.delete(k);
      else next.set(k, String(v));
    });
    if (!keepPage) next.delete('page');
    setSearchParams(next, { replace: true });
  };

  // The search box follows the address (a link, Back), and types into it after a pause.
  useEffect(() => { setSearchText(query.search || ''); }, [query.search]);
  useEffect(() => {
    const typed = searchText.trim();
    if (typed === (query.search || '')) return undefined;
    const t = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (typed) next.set('search', typed); else next.delete('search');
      next.delete('page');
      setSearchParams(next, { replace: true });
    }, 350);
    return () => clearTimeout(t);
  }, [searchText, query.search, searchParams, setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    axios.get(apiUrl('/api/repayments'), {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      params: { ...query, page_size: PAGE_SIZE },
    })
      .then((res) => { if (!cancelled) setData(res.data || null); })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.error || err?.message || 'The repayments could not be loaded.');
        setData(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [query, reloadKey]);

  const items = Array.isArray(data?.items) ? data.items : [];
  const page = Number(data?.page) || Number(query.page) || 1;
  const pages = Number(data?.pages) || 1;
  const methods = Array.isArray(data?.methods) ? data.methods : [];
  const totals = data?.totals || { count: 0, amount: 0 };
  const filtering = Boolean(query.search || query.method || query.from || query.to || query.loan_id || query.client_id);
  const scopedLoan = query.loan_id ? items.find((r) => String(r.loan_id) === String(query.loan_id)) : null;
  const scopedClient = query.client_id ? items.find((r) => String(r.client_id) === String(query.client_id)) : null;
  const goPage = (p) => setFilters({ page: p > 1 ? p : '' }, { keepPage: true });

  const columns = [
    { field: 'amount_paid', header: 'Amount', formatter: (v, row) => (
        <RepaymentLink repayment={row} className="text-base font-bold !text-green-700 dark:!text-green-400">
          {toCurrency(v)}
        </RepaymentLink>
      ) },
    { field: 'payment_date', header: 'Date', formatter: (v) => formatDateDMY(v) },
    { field: 'payment_method', header: 'Method', formatter: (v) => v || '-' },
    { field: 'payment_reference', header: 'Reference', formatter: (v) => (
        v ? <span className="font-mono">{v}</span> : <span className="text-gray-400 dark:text-slate-500">None</span>
      ) },
    { field: 'loan_code', header: 'Loan', formatter: (v, row) => (
        <LoanLink loanId={row.loan_id}>{v || `Loan #${row.loan_id}`}</LoanLink>
      ) },
    { field: 'client_name', header: 'Client', formatter: (v, row) => (
        <ClientLink clientId={row.client_id} name={v || '-'} />
      ) },
    { field: 'entered_by', header: 'Entered by', formatter: (v) => v || '-' },
  ];

  return (
    <div className="max-w-full min-h-screen">
      {/* Totals of everything the filters select (all pages), from the API. */}
      <div className="grid gap-4 mb-3 grid-cols-1 sm:grid-cols-2">
        <KpiCard
          label={filtering ? 'Repayments (filtered)' : 'Repayments'}
          value={Number(totals.count || 0).toLocaleString()}
          color="blue"
          icon={<Hash size={18} />}
        />
        <KpiCard
          label={filtering ? 'Total paid (filtered)' : 'Total paid'}
          value={toCurrency(totals.amount)}
          color="green"
          icon={<Banknote size={18} />}
        />
      </div>

      {/* Toolbar: search and filters on the left, paging hard right. */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap items-center gap-2 flex-1 min-w-[240px]">
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search client, ID number, loan code or reference…"
            aria-label="Search repayments"
            className="toolbar-control flex-1 min-w-[200px]"
          />
          <select
            value={query.method || ''}
            onChange={(e) => setFilters({ method: e.target.value })}
            aria-label="Payment method"
            className="toolbar-control min-w-[160px]"
          >
            <option value="">All methods</option>
            {methods.map((m) => <option key={m} value={m}>{m}</option>)}
            {query.method && !methods.includes(query.method) && <option value={query.method}>{query.method}</option>}
          </select>
          <label className="text-xs text-gray-500 dark:text-slate-400" htmlFor="repayments-from">From</label>
          <input
            id="repayments-from"
            type="date"
            value={query.from || ''}
            max={query.to || undefined}
            onChange={(e) => setFilters({ from: e.target.value })}
            className="toolbar-control w-40"
          />
          <label className="text-xs text-gray-500 dark:text-slate-400" htmlFor="repayments-to">To</label>
          <input
            id="repayments-to"
            type="date"
            value={query.to || ''}
            min={query.from || undefined}
            onChange={(e) => setFilters({ to: e.target.value })}
            className="toolbar-control w-40"
          />
          <select
            value={query.sort || 'newest'}
            onChange={(e) => setFilters({ sort: e.target.value === 'newest' ? '' : e.target.value })}
            aria-label="Sort"
            className="toolbar-control min-w-[150px]"
          >
            {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          {canRedo && (
            <button
              type="button"
              onClick={() => setUndoCtx({ mode: 'redo' })}
              className="toolbar-control px-3 text-sm font-medium rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300"
            >
              Redo Repayment
            </button>
          )}
          {filtering && (
            <button
              type="button"
              onClick={() => { setSearchText(''); setSearchParams(query.sort ? { sort: query.sort } : {}, { replace: true }); }}
              className="toolbar-control px-2 text-sm text-blue-600 dark:text-blue-300 hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>
        <Pager page={page} pages={pages} onPage={goPage} className="ml-auto" />
      </div>

      {/* A loan or client the page was opened for ("Open full page", a link). */}
      {(query.loan_id || query.client_id) && (
        <div className="mb-3 flex flex-wrap gap-2">
          {query.loan_id && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800/60">
              Loan: {scopedLoan?.loan_code || `#${query.loan_id}`}
              <button type="button" onClick={() => setFilters({ loan_id: '' })} aria-label="Show all loans" title="Show all loans"><X size={12} /></button>
            </span>
          )}
          {query.client_id && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800/60">
              Client: {scopedClient?.client_name || `#${query.client_id}`}
              <button type="button" onClick={() => setFilters({ client_id: '' })} aria-label="Show all clients" title="Show all clients"><X size={12} /></button>
            </span>
          )}
        </div>
      )}

      {error && <div className="p-2 rounded-lg mb-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300" role="alert">{error}</div>}
      {notice && (
        <div className="p-2 rounded-lg mb-2 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 flex items-center justify-between" role="status">
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice('')} className="px-2 text-lg leading-none" aria-label="Dismiss">×</button>
        </div>
      )}
      {undoCtx && (
        <UndoRepaymentConfirm
          ctx={undoCtx}
          mode={undoCtx.mode || 'undo'}
          onClose={() => setUndoCtx(null)}
          onSuccess={() => {
            const redone = undoCtx.mode === 'redo';
            setUndoCtx(null);
            setNotice(redone ? 'Repayment put back' : 'Repayment undone');
            setReloadKey((k) => k + 1);
          }}
        />
      )}

      <div className="bg-white dark:bg-slate-900">
        <ResponsiveTable
          data={items}
          columns={columns}
          isLoading={loading && !data}
          onRowClick={(row) => openRepayment(row)}
          actions={[{
            label: 'Undo Repayment',
            // On the repayments the API says may be undone (staff allowed to
            // undo, on a loan whose status allows it); the form opens on this one.
            condition: (row) => row.can_undo === true,
            className: 'bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300',
            onClick: (row) => setUndoCtx({ loanId: row.loan_id, loanCode: row.loan_code, repaymentId: row.id }),
          }]}
          emptyMessage={filtering ? 'No repayments match these filters.' : 'No repayments yet.'}
        />
      </div>
    </div>
  );
};

export default Repayments;
